import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './StockDetailsGrid.css';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import eFormType from '../../../main/assets/enums/eFormType';
import DeleteModal from '../../../main/components/modal/delete/DeleteModal';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import IProductStock from '../../../main/interfaces/IProductStock';
import StockInventory from './stockInventory/StockInventory';
import axiosInstance from '../../../main/initializers/axios';
import IProduct from '../../../main/interfaces/IProduct';
import EditModal from '../../../main/components/modal/edit/EditModal';
import IPagination from '../../../main/interfaces/IPagination';
import IGridItems from '../../../main/interfaces/IGridItems';
import GeneralTable from '../../../main/components/grid/GeneralTable';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CategoryIcon from '@mui/icons-material/Category';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useTranslation } from 'react-i18next';

const StockDetailsGrid = () => {
  const [productStocks, setProductStocks] = useState<IProductStock[]>([]);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState<boolean>(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState<boolean>(false);
  const [selectedVal, setSelectedVal] = useState<IProductStock | null>(null);
  const [approved, setApproved] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const { i18n } = useTranslation();

  const getProductName = (ps: IProductStock): string => ps.product?.name || 'Unknown Product';
  const getProductType = (ps: IProductStock): number | undefined => ps.product?.productCategory?.type;
  const getProductCategory = (ps: IProductStock): string => ps.product?.productCategory?.name || '';

  const getProductTypeCategory = (ps: IProductStock): 'restaurant' | 'bar' | 'other' => {
    const type = getProductType(ps);
    if (type === 1) return 'restaurant';
    if (type === 2) return 'bar';

    const cat = getProductCategory(ps).toLowerCase();
    const name = getProductName(ps).toLowerCase();

    if (/(food|meal|dish|dessert|appetizer)/.test(cat)) return 'restaurant';
    if (/(drink|beverage|alcohol|cocktail)/.test(cat)) return 'bar';

    const restaurantKeywords = ['food', 'meal', 'dish', 'pasta', 'pizza', 'burger', 'steak', 'salad', 'soup', 'dessert'];
    const barKeywords = ['drink', 'beer', 'wine', 'cocktail', 'whiskey', 'vodka', 'gin', 'rum', 'tequila', 'soda'];

    if (restaurantKeywords.some((k) => name.includes(k))) return 'restaurant';
    if (barKeywords.some((k) => name.includes(k))) return 'bar';

    return 'other';
  };

  const getStockStatus = (ps: IProductStock): 'in-stock' | 'low-stock' | 'out-of-stock' => {
    const current = ps.quantity ?? 0;
    const min = ps.minQuantity ?? 0;

    if (current === 0) return 'out-of-stock';
    if (current <= min) return 'low-stock';
    return 'in-stock';
  };

  const getStock = useCallback(() => {
    axiosInstance.get(ApiConstants.Products.list).then((res) => {
      setProducts(res.data ?? []);
    });
  }, []);

  const getTable = useCallback(
    (pag?: IPagination, gridOverride?: IGridItems[]) => {
      const effective = pag ?? pagination;
      const effectiveGrid = gridOverride ?? gridItems ?? [];

      axiosInstance.post(ApiConstants.stock.listct, { gridItems: effectiveGrid, pagination: effective }).then((res) => {
        const items = res.data?.items ?? [];
        const p = res.data?.pagination ?? {};

        setProductStocks(items);
        setGridItems(res.data?.gridItems ?? []);

        setPagination((prev) => ({
          ...prev,
          count: p.count ?? prev.count,
          page: p.page ?? prev.page,
          itemPerPage: p.itemPerPage ?? prev.itemPerPage,
          itemsPerPage: p.itemsPerPage ?? prev.itemsPerPage,
        }));
      });
    },
    [gridItems, pagination],
  );

  const selectedItemPagination = (pag: IPagination) => {
    setPagination(pag);
    getTable(pag);
  };

  const deleteDataSet = (id: number) => {
    axiosInstance.post(ApiConstants.stock.delete, { Id: id }).then(() => {
      setIsModalDeleteOpen(false);
      setProductStocks((prev) => prev.filter((x) => x.id !== id));
    });
  };

  const filteredProducts = useMemo(() => {
    return productStocks.filter((ps) => {
      const typeCat = getProductTypeCategory(ps);
      const status = getStockStatus(ps);

      const matchesCategory =
        categoryFilter === 'all' ||
        (categoryFilter === 'restaurant' && typeCat === 'restaurant') ||
        (categoryFilter === 'bar' && typeCat === 'bar') ||
        (categoryFilter === 'other' && typeCat === 'other');

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'in-stock' && status === 'in-stock') ||
        (statusFilter === 'low-stock' && status === 'low-stock') ||
        (statusFilter === 'out-of-stock' && status === 'out-of-stock');

      return matchesCategory && matchesStatus;
    });
  }, [productStocks, statusFilter, categoryFilter]);

  const stats = useMemo(() => {
    const total = productStocks.length;
    const inStock = productStocks.filter((p) => getStockStatus(p) === 'in-stock').length;
    const lowStock = productStocks.filter((p) => getStockStatus(p) === 'low-stock').length;
    const outStock = productStocks.filter((p) => getStockStatus(p) === 'out-of-stock').length;
    const restaurant = productStocks.filter((p) => getProductTypeCategory(p) === 'restaurant').length;
    const bar = productStocks.filter((p) => getProductTypeCategory(p) === 'bar').length;

    return { total, inStock, lowStock, outStock, restaurant, bar };
  }, [productStocks]);

  const hasActiveFilters = statusFilter !== 'all' || categoryFilter !== 'all';

  useEffect(() => {
    getTable();
    getStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (item: IProductStock) => {
    setSelectedVal(item);
    setIsModalEditOpen(true);
  };

  const handleDelete = (item: IProductStock) => {
    setSelectedVal(item);
    setIsModalDeleteOpen(true);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setCategoryFilter('all');
  };

  return (
    <BasicCli>
      <div className="sdWrap bcPageContent">
        <div className="sdContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{i18n.t('stockDetails.stockManagment')}</h1>
            <div className="bcPageHeaderActions">
              <div className="sdField">
                <FilterListIcon className="sdFieldIcon" />
                <select className="ui-control ui-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">{i18n.t('stockDetails.stockStatus')}</option>
                  <option value="in-stock">{i18n.t('stockDetails.inStock')}</option>
                  <option value="low-stock">{i18n.t('stockDetails.lowStock')}</option>
                  <option value="out-of-stock">{i18n.t('stockDetails.outOfStock')}</option>
                </select>
              </div>

              <div className="sdField">
                <CategoryIcon className="sdFieldIcon" />
                <select className="ui-control ui-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <option value="all">{i18n.t('generic.Categorie')}</option>
                  <option value="restaurant">{i18n.t('menu.Restaurant')}</option>
                  <option value="bar">Bar</option>
                  <option value="other">{i18n.t('stockDetails.other')}</option>
                </select>
              </div>

              <button className="ui-control ui-btn ui-btn--primary sdAddBtn" onClick={() => setIsModalOpen(true)}>
                <AddIcon />
                <span>{i18n.t('stockDetails.addStock')}</span>
              </button>
            </div>
          </div>

          <div className="sdCards">
            <div className="bcCard sdCard">
              <div className="sdIconWrap">
                <InventoryIcon fontSize="large" className="sdIcon" />
              </div>
              <h3 className="sdCardTitle">{i18n.t('generic.totalProducts')}</h3>
              <p className="sdValue">{stats.total}</p>
              <div className="sdSub">
                <span className="sdBadge sdBadge--primary">
                  {filteredProducts.length} {i18n.t('orderDetails.filtered')}
                </span>
              </div>
            </div>

            <div className="bcCard sdCard">
              <div className="sdIconWrap">
                <CheckCircleIcon fontSize="large" className="sdIcon" />
              </div>
              <h3 className="sdCardTitle">{i18n.t('stockDetails.inStock')}</h3>
              <p className="sdValue">{stats.inStock}</p>
              <div className="sdSub">
                <span className="sdBadge sdBadge--success">{i18n.t('stockDetails.goodInventory')}</span>
              </div>
            </div>

            <div className="bcCard sdCard">
              <div className="sdIconWrap">
                <WarningIcon fontSize="large" className="sdIcon" />
              </div>
              <h3 className="sdCardTitle">{i18n.t('stockDetails.lowStock')}</h3>
              <p className="sdValue">{stats.lowStock}</p>
              <div className="sdSub">
                <span className="sdBadge sdBadge--warning">{i18n.t('stockDetails.needsRestocking')}</span>
              </div>
            </div>

            <div className="bcCard sdCard">
              <div className="sdIconWrap">
                <InventoryIcon fontSize="large" className="sdIcon" />
              </div>
              <h3 className="sdCardTitle">{i18n.t('stockDetails.outOfStock')}</h3>
              <p className="sdValue">{stats.outStock}</p>
              <div className="sdSub">
                <span className="sdBadge sdBadge--danger">{i18n.t('stockDetails.urgentRestock')}</span>
              </div>
            </div>

            <div className="bcCard sdCard">
              <div className="sdIconWrap">
                <RestaurantIcon fontSize="large" className="sdIcon" />
              </div>
              <h3 className="sdCardTitle">{i18n.t('menu.Restaurant')}</h3>
              <p className="sdValue">{stats.restaurant}</p>
              <div className="sdSub">
                <span className="sdBadge sdBadge--danger">{i18n.t('stockDetails.foodItems')}</span>
              </div>
            </div>

            <div className="bcCard sdCard">
              <div className="sdIconWrap">
                <LocalBarIcon fontSize="large" className="sdIcon" />
              </div>
              <h3 className="sdCardTitle">Bar</h3>
              <p className="sdValue">{stats.bar}</p>
              <div className="sdSub">
                <span className="sdBadge sdBadge--primary">{i18n.t('stockDetails.drinkItems')}</span>
              </div>
            </div>
          </div>

          {hasActiveFilters ? (
            <div className="ui-panel sdFilters">
              <div className="sdFilterStatus">
                <span className="bcMuted">
                  Showing <strong className="bcText">{filteredProducts.length}</strong> of <strong className="bcText">{stats.total}</strong> products
                </span>

                <button className="ui-control ui-btn sdClearBtn" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            </div>
          ) : null}

          <div className=" sdTable">
            <GeneralTable
              data={filteredProducts}
              columns={gridItems}
              gridEntity={6}
              title=""
              hasRp={true}
              selectedItem={(val: any) => setSelectedVal(val)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              pagination={pagination}
              selectedItemPagination={(val: any) => selectedItemPagination(val)}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                getTable(undefined, nextGridItems);
              }}
            />
          </div>

          {isModalDeleteOpen && (
            <DeleteModal width="500px" handleClose={() => setIsModalDeleteOpen(false)} handleDelete={() => deleteDataSet(selectedVal?.id ?? 0)}>
              {i18n.t('stockDetails.areYouSureYouWantToDeleteStockItem')} <strong className="text-danger"> #{selectedVal?.id}</strong>?
            </DeleteModal>
          )}

          {isModalEditOpen && (
            <EditModal
              title={i18n.t('stockDetails.editStock')}
              width="1024"
              closeModal={() => setIsModalEditOpen(false)}
              onSubmitData={() => setApproved(true)}
              setIsModalOpen={setIsModalEditOpen}
              modalType={eFormType.Edit}
            >
              <StockInventory
                products={products}
                tempProduct={selectedVal}
                createOrUpdate={approved}
                modalType={eFormType.Edit}
                onSubmitData={() => {
                  setApproved(false);
                  getTable();
                }}
              />
            </EditModal>
          )}

          {isModalOpen && (
            <EditModal
              title={i18n.t('stockDetails.addStock')}
              width="80%"
              closeModal={() => setIsModalOpen(false)}
              onSubmitData={() => setApproved(true)}
              setIsModalOpen={setIsModalOpen}
              modalType={eFormType.New}
            >
              <StockInventory
                createOrUpdate={approved}
                products={products}
                modalType={eFormType.New}
                onSubmitData={() => {
                  setApproved(false);
                  getTable();
                  setIsModalOpen(false);
                }}
              />
            </EditModal>
          )}
        </div>
      </div>
    </BasicCli>
  );
};

export default StockDetailsGrid;
